import { lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PageTitle from "common/Components/PageTitle/PageTitle";
import AppDialog, {
  AppDialogCancelButton,
  AppDialogConfirmButton,
} from "common/Components/AppDialog";
import UserContext from "common/Context/UserContext";
import {
  fetchMeCustomerAccessRows,
  syncProfileCustomerAccessSession,
} from "common/Auth/InitializeUserSessionFromMe";
import {
  fetchAppActiveCustomers,
  resetAppActiveCustomersCache,
} from "common/Services/ActiveCustomersMaster";
import { clearCustomerContextHeaders } from "common/HttpClient/CustomerContextHeaders";
import { useCustomersViewInitialization } from "./Customers.hook";
import { CustomersServices } from "./Customers.services";
import GROUPS_CONSTANTS, { CUSTOMERS_PAGE_CONSTANTS } from "./Customers.constants";
import { SelectedCustomerAccessProvider } from "./Components/SelectedCustomerAccessContext";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import {
  AddCustomerButton,
  CustomersPageRoot,
} from "./Customers.styles";
import CustomersList from "./CustomersList";
import CustomerDetailsDrawer from "./CustomerDetailsDrawer";
import AddCustomerDrawer from "./AddCustomerDrawer";

const NoDataFound = lazy(() => import("common/Components/NoDataFound"));
const Toast = lazy(() => import("common/Components/Toast"));

const STATUS_CONFIRM_DIALOG_TITLE_ID = "customer-status-confirm-dialog-title";

const Customers = () => {
  const { state, actions } = useCustomersViewInitialization();
  const { setCustomersList, setActiveCustomersList, userProfile, loadUserInformation } =
    useContext(UserContext);
  const Constants = INFRAMARK_CONSTANTS.groups.constants;
  const customersServices = useMemo(() => CustomersServices(), []);

  const checkCustomerExists = useCallback(
    (params) => customersServices.checkCustomerExists(params),
    [customersServices]
  );
  const [toast, setToast] = useState({
    show: false,
    type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
    title: "",
    description: "",
  });
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertInfo, setAlertInfo] = useState({});
  const [accessDrawerOpen, setAccessDrawerOpen] = useState(false);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [selectedGroupForAccess, setSelectedGroupForAccess] = useState(null);
  const [dataConnectionsApplying, setDataConnectionsApplying] = useState(false);
  const selectedGroupForAccessRef = useRef(selectedGroupForAccess);
  selectedGroupForAccessRef.current = selectedGroupForAccess;

  const dismissToast = useCallback(() => {
    setToast((t) => ({ ...t, show: false }));
  }, []);

  useEffect(() => {
    if (state.error) {
      setToast({
        show: true,
        type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
        title: GROUPS_CONSTANTS.groups.TOAST_ERROR_TITLE,
        description: state.error,
      });
    }
  }, [state.error]);

  useEffect(() => {
    const selectedCustomer = localStorage.getItem("selected_customer");
    if (selectedCustomer) {
      localStorage.removeItem("selected_customer");
    }
    clearCustomerContextHeaders();
  }, []);

  useEffect(() => {
    if (!accessDrawerOpen) return;
    const current = selectedGroupForAccessRef.current;
    if (current == null) return;
    const id = current.code;
    const refreshed = (state.customersList || []).find(
      (g) => String(g?.code) === String(id)
    );
    if (refreshed) {
      setSelectedGroupForAccess((prev) =>
        prev != null && String(prev.code) === String(id) ? refreshed : prev
      );
    }
  }, [state.customersList, accessDrawerOpen]);

  const refreshAfterStatusChange = useCallback(async () => {
    const rows = await fetchMeCustomerAccessRows();
    if (rows) {
      setCustomersList?.(rows);
      await syncProfileCustomerAccessSession(
        userProfile?.profile,
        rows,
        loadUserInformation
      );
    }
    actions.getAllCustomers();
  }, [actions, loadUserInformation, setCustomersList, userProfile?.profile]);

  const handleCreateCustomer = (groupInfo, onFormReset) => {
    actions.addNewCustomer(groupInfo, async () => {
      onFormReset?.();
      setCreateDrawerOpen(false);
      setToast({
        show: true,
        type: GROUPS_CONSTANTS.toast.TYPE_SUCCESS,
        title: GROUPS_CONSTANTS.toast.TITLE_SUCCESS,
        description: GROUPS_CONSTANTS.alert.CUSTOMER_CREATED_SUCCESS,
      });
      actions.getAllCustomers(0, state.pagination.size);
      // Refresh active customers so Invite User / Platform User Access pick up the new customer.
      resetAppActiveCustomersCache();
      const activeCustomers = await fetchAppActiveCustomers();
      setActiveCustomersList?.(activeCustomers);
    });
  };

  const handleToggleActive = (status, group) => {
    const title = status
      ? GROUPS_CONSTANTS.groupsList.ACTIVATE_CUSTOMER
      : GROUPS_CONSTANTS.groupsList.DEACTIVATE_CUSTOMER;
    const message = status
      ? GROUPS_CONSTANTS.groupsList.ACTIVATE_CONFIRM_MESSAGE
      : GROUPS_CONSTANTS.groupsList.DEACTIVATE_CONFIRM_MESSAGE;
    setAlertInfo({
      title,
      message,
      info: { isDeleted: !status },
      code: group.code,
    });
    setAlertVisible(true);
  };

  const handleCloseStatusConfirm = () => {
    setAlertVisible(false);
    setAlertInfo({});
  };

  const handleConfirmStatusChange = () => {
    actions.patchCustomerInfo(
      alertInfo.info,
      alertInfo.code,
      Constants.GROUPLISTVIEW,
      refreshAfterStatusChange
    );
    setAlertVisible(false);
  };

  const handleDataConnectionsApply = useCallback(
    async (payload) => {
      const customerCode =
        selectedGroupForAccess?.code ?? selectedGroupForAccess?.customerCode;
      setDataConnectionsApplying(true);
      setToast((t) => ({ ...t, show: false }));
      try {
        const res = await customersServices.patchCustomerDataConnections(
          customerCode,
          payload
        );
        if (res?.error) {
          const errorMessage =
            res?.error?.trim() || GROUPS_CONSTANTS.toast.MESSAGE_FAILURE;
          setToast({
            show: true,
            type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
            title: GROUPS_CONSTANTS.groups.TOAST_ERROR_TITLE,
            description: errorMessage,
          });
          return { ok: false, error: errorMessage };
        }
        if (res?.status === 200 || res?.status === 204) {
          setToast({
            show: true,
            type: GROUPS_CONSTANTS.toast.TYPE_SUCCESS,
            title: GROUPS_CONSTANTS.toast.TITLE_SUCCESS,
            description:
              GROUPS_CONSTANTS.groupsList.DATA_CONNECTIONS_UPDATE_SUCCESS,
          });
          actions.getAllCustomers();
          return { ok: true };
        }
        return { ok: false };
      } catch (err) {
        const errorMessage =
          err?.error?.trim() || GROUPS_CONSTANTS.toast.MESSAGE_FAILURE;
        setToast({
          show: true,
          type: GROUPS_CONSTANTS.toast.TYPE_FAILURE,
          title: GROUPS_CONSTANTS.groups.TOAST_ERROR_TITLE,
          description: errorMessage,
        });
        return { ok: false, error: errorMessage };
      } finally {
        setDataConnectionsApplying(false);
      }
    },
    [
      actions,
      customersServices,
      selectedGroupForAccess?.code,
      selectedGroupForAccess?.customerCode,
    ]
  );

  return (
    <CustomersPageRoot>
      <Suspense fallback={null}>
        <Toast
          type={toast.type}
          title={toast.title}
          description={toast.description}
          show={toast.show}
          onClose={dismissToast}
          duration={GROUPS_CONSTANTS.toast.DURATION}
        />
      </Suspense>

      <AppDialog
        open={alertVisible}
        onClose={handleCloseStatusConfirm}
        aria-labelledby={STATUS_CONFIRM_DIALOG_TITLE_ID}
        heading={alertInfo.title}
        footer={
          <>
            <AppDialogCancelButton type="button" onClick={handleCloseStatusConfirm}>
              Cancel
            </AppDialogCancelButton>
            <AppDialogConfirmButton
              type="button"
              variant="contained"
              colorVariant={alertInfo.info?.isDeleted ? "danger" : "primary"}
              onClick={handleConfirmStatusChange}
              autoFocus
            >
              {alertInfo.info?.isDeleted
                ? GROUPS_CONSTANTS.groupsList.BUTTON_DEACTIVATE
                : GROUPS_CONSTANTS.groupsList.BUTTON_ACTIVATE}
            </AppDialogConfirmButton>
          </>
        }
      >
        {alertInfo.message}
      </AppDialog>

      <PageTitle
        title={CUSTOMERS_PAGE_CONSTANTS.PAGE_TITLE}
        subTitle={CUSTOMERS_PAGE_CONSTANTS.PAGE_SUBTITLE}
        icon={<BusinessOutlinedIcon />}
        action={
          <AddCustomerButton
            type="button"
            startIcon={<AddIcon />}
            onClick={() => {
              setAccessDrawerOpen(false);
              setSelectedGroupForAccess(null);
              setCreateDrawerOpen(true);
            }}
          >
            {CUSTOMERS_PAGE_CONSTANTS.ADD_CUSTOMER_LABEL}
          </AddCustomerButton>
        }
      />

      {state.error && (!state.customersList || state.customersList.length === 0) ? (
        <Suspense fallback={<div>{GROUPS_CONSTANTS.loading.FALLBACK}</div>}>
          <NoDataFound
            message={GROUPS_CONSTANTS.groups.NO_CUSTOMERS_FOUND}
            onRefresh={() => actions.getAllCustomers()}
          />
        </Suspense>
      ) : (
        <CustomersList
          groups={state.customersList || []}
          loading={state.loading}
          pagination={state.pagination}
          listFilters={state.listFilters}
          onPageChange={actions.onPageChange}
          onRowsPerPageChange={actions.onRowsPerPageChange}
          onListFiltersChange={actions.onListFiltersChange}
          onToggleActive={handleToggleActive}
          onRowOpen={(group) => {
            setCreateDrawerOpen(false);
            setSelectedGroupForAccess(group);
            setAccessDrawerOpen(true);
          }}
        />
      )}

      <SelectedCustomerAccessProvider value={selectedGroupForAccess}>
        <CustomerDetailsDrawer
          open={accessDrawerOpen}
          onClose={() => {
            setAccessDrawerOpen(false);
            setSelectedGroupForAccess(null);
          }}
          customerName={selectedGroupForAccess?.name ?? ""}
          customerCode={
            selectedGroupForAccess?.code ?? selectedGroupForAccess?.customerCode
          }
          isDeleted={Boolean(selectedGroupForAccess?.isDeleted)}
          modules={selectedGroupForAccess?.modules}
          onModulesUpdated={(modules) => {
            setSelectedGroupForAccess((prev) =>
              prev != null ? { ...prev, modules } : prev
            );
          }}
          onDataConnectionsApply={handleDataConnectionsApply}
          dataConnectionsApplying={dataConnectionsApplying}
          groups={state.customersList || []}
          getAllCustomers={actions.getAllCustomers}
        />
      </SelectedCustomerAccessProvider>

      <AddCustomerDrawer
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        groups={state.customers || state.customersList || []}
        saving={state.loading && createDrawerOpen}
        checkCustomerExists={checkCustomerExists}
        onSubmit={handleCreateCustomer}
      />
    </CustomersPageRoot>
  );
};

export default Customers;
